/**
 * Customer-attempt reason enums + pure classifiers for the applicant SPA's
 * fire-and-forget attempt reporting (see ./reportAttempt).
 *
 * An "attempt" here is a customer COMMITTING a capture that fails — a rejected
 * shot (self-retake) or a server-rejected upload. The `reason_code` is a STABLE
 * client-side enum: the console renders the Thai label from the code, and the
 * server aggregates the applicant journey by `step_key` + `reason_code`. Keep
 * these codes stable — they are a reporting contract, not UI copy.
 *
 * Everything here is a pure function (input → reason code, no DOM), mirroring the
 * `assessPixels` pattern in ./livenessQuality so it stays vitest-friendly.
 */
import type { PositionDenial } from './geolocation';
import type { QualityIssue } from './livenessQuality';

/** Stable reason codes for a failed DOCUMENT capture attempt. */
export type DocumentReason =
  | 'blur'
  | 'glare'
  | 'out_of_frame'
  | 'unreadable'
  | 'wrong_document'
  | 'expired'
  | 'no_face_in_doc'
  | 'retaken'
  | 'server_rejected';

/**
 * Stable reason codes for a failed LIVENESS capture attempt.
 *
 * `glare` is included so the quality classifier's `tooBright → glare` mapping is
 * representable (an over-exposed/reflective selfie is a real liveness failure);
 * it mirrors the DOCUMENT `glare` code.
 */
export type LivenessReason =
  | 'no_face'
  | 'out_of_frame'
  | 'low_light'
  | 'glare'
  | 'liveness_fail'
  | 'face_mismatch'
  | 'connection_lost'
  | 'cancelled'
  | 'retaken'
  | 'server_rejected';

/**
 * Stable reason codes for a refused DEVICE POSITION.
 *
 * Identical to `PositionDenial` in ./geolocation and deliberately restated
 * rather than imported: these are a REPORTING contract the console and the
 * server aggregate on, and the UI enum is free to gain a case (say, a
 * distinction between two kinds of timeout) without silently changing what a
 * stored `reason_code` means. The two are kept in step by
 * {@link geolocationReason}, which fails to compile if they diverge.
 */
export type GeolocationReason =
  | 'permission_denied'
  | 'position_unavailable'
  | 'timeout'
  | 'unsupported'
  | 'insecure_context';

/**
 * The statechart LEAF state keys the server aggregates the journey by. These are
 * the EXACT wire values — never short forms like "id_front".
 */
export const ATTEMPT_STEP_KEYS = [
  'awaiting_consent',
  'awaiting_id_front',
  'awaiting_id_back',
  // kyc_thai_v4 v4: the document is CHOSEN in this step (Thai ID, licence or
  // passport), so its front is captured here rather than in awaiting_id_front.
  'awaiting_document',
  'awaiting_driving_license_back',
  'awaiting_liveness',
  'awaiting_signature',
] as const;
export type AttemptStepKey = (typeof ATTEMPT_STEP_KEYS)[number];

export function isAttemptStepKey(key: string | undefined): key is AttemptStepKey {
  return (ATTEMPT_STEP_KEYS as readonly string[]).includes(key ?? '');
}

/**
 * Project a UI denial onto the stable reporting enum. Pure, and identity today.
 *
 * The point is the TYPES, not the body: this signature is what makes the
 * reporting contract and the UI enum provably the same set, so a future case
 * added to `PositionDenial` cannot reach a stored `reason_code` without someone
 * deciding what it should be called on the wire.
 */
export function geolocationReason(denial: PositionDenial): GeolocationReason {
  return denial;
}

/**
 * Map a client-side selfie quality issue to a stable liveness reason code. Pure.
 * The quality heuristics ({@link QualityIssue}) are UX-only luma guards; this
 * projects them onto the reporting enum so a blocked-submit (quality gate) is
 * bucketed by WHY the shot was rejected.
 */
export function classifyLivenessQuality(issue: QualityIssue): LivenessReason {
  switch (issue) {
    case 'tooDark':
      return 'low_light';
    case 'tooBright':
      return 'glare';
    case 'lowDetail':
      return 'liveness_fail';
  }
}
